/**
 * Status strip for one forge: status chip, runtime generation, start/stop,
 * the model tier select (balanced / powerful / frontier → PATCH) and the task
 * size select (standard / large → PATCH, only while stopped or failed).
 *
 * `frontier` and `large` are Pro-only; the server refuses them with a 403
 * the hook surfaces through `error`, so the controls never pretend to know
 * the subscription — they show the cost hint and let the server decide.
 */
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, Play, Square } from 'lucide-react';
import type { ForgeModelTier, ForgeSize, ForgeStatus, ForgeSummary } from '../types';

export const FORGE_STATUS_LABELS: Record<ForgeStatus, string> = {
  provisioning: 'Starting',
  running: 'Running',
  stopping: 'Stopping',
  stopped: 'Stopped',
  archived: 'Archived',
  failed: 'Failed',
  deleted: 'Deleted',
};

export const FORGE_STATUS_CLASSES: Record<ForgeStatus, string> = {
  provisioning: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20',
  running: 'bg-green-500/10 text-green-600 border-green-500/20',
  stopping: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20',
  stopped: 'bg-gray-500/10 text-gray-600 border-gray-500/20',
  archived: 'bg-gray-500/10 text-gray-500 border-gray-500/20',
  failed: 'bg-red-500/10 text-red-600 border-red-500/20',
  deleted: 'bg-gray-500/10 text-gray-500 border-gray-500/20',
};

/** The three model tiers, in ascending cost, with the one-line hint each shows. */
export const FORGE_MODEL_TIER_OPTIONS: ReadonlyArray<{
  value: ForgeModelTier;
  label: string;
  hint: string;
}> = [
  {
    value: 'balanced',
    label: 'Balanced',
    hint: 'The default. Fast and capable; the lowest cost per turn.',
  },
  {
    value: 'powerful',
    label: 'Powerful',
    hint: 'Deeper reasoning for hard problems. Roughly 5× the cost per turn.',
  },
  {
    value: 'frontier',
    label: 'Frontier',
    hint: "Anthropic's most capable model. The highest cost per turn — Pro plan only.",
  },
];

/** The two task sizes with what each buys. */
export const FORGE_SIZE_OPTIONS: ReadonlyArray<{ value: ForgeSize; label: string; hint: string }> =
  [
    { value: 'standard', label: 'Standard', hint: '2 vCPU · 4 GB · 40 GB workspace' },
    {
      value: 'large',
      label: 'Large',
      hint: '4 vCPU · 8 GB · 80 GB workspace. Twice the hourly rate — Pro plan only.',
    },
  ];

const SELECT_CLASS =
  'h-8 rounded-md border border-input bg-background px-2 text-sm disabled:opacity-60';

export function ForgeStatusChip({ status }: { status: ForgeStatus }) {
  const transitional = status === 'provisioning' || status === 'stopping';
  return (
    <Badge variant="outline" className={FORGE_STATUS_CLASSES[status]}>
      {transitional && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
      {FORGE_STATUS_LABELS[status]}
    </Badge>
  );
}

/** Whether the size may change: only while no runtime holds the current one. */
export function canChangeForgeSize(status: ForgeStatus): boolean {
  return status === 'stopped' || status === 'failed';
}

interface ForgeStatusBarProps {
  forge: ForgeSummary;
  onStart: () => void;
  onStop: () => void;
  /** `null` clears the opt-in back to the tier default (balanced). */
  onSetModelTier: (tier: ForgeModelTier | null) => void;
  onSetSize: (size: ForgeSize) => void;
  isMutating: boolean;
}

export function ForgeStatusBar({
  forge,
  onStart,
  onStop,
  onSetModelTier,
  onSetSize,
  isMutating,
}: ForgeStatusBarProps) {
  const canStart = forge.status === 'stopped' || forge.status === 'failed';
  const canStop = forge.status === 'running' || forge.status === 'provisioning';
  const modelTier: ForgeModelTier = forge.modelOptIn ?? 'balanced';
  const tierOption =
    FORGE_MODEL_TIER_OPTIONS.find((o) => o.value === modelTier) ?? FORGE_MODEL_TIER_OPTIONS[0];
  const sizeOption =
    FORGE_SIZE_OPTIONS.find((o) => o.value === forge.size) ?? FORGE_SIZE_OPTIONS[0];
  const sizeLocked = !canChangeForgeSize(forge.status);

  return (
    <div className="rounded-lg border p-3 space-y-3" data-testid="forge-status-bar">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <ForgeStatusChip status={forge.status} />
          <span className="text-xs text-muted-foreground" title="Runtime generation">
            gen {forge.runtimeGeneration}
          </span>
        </div>
        {canStart && (
          <Button size="sm" onClick={onStart} disabled={isMutating}>
            <Play className="h-4 w-4 mr-1" />
            Start
          </Button>
        )}
        {canStop && (
          <Button size="sm" variant="outline" onClick={onStop} disabled={isMutating}>
            <Square className="h-4 w-4 mr-1" />
            Stop
          </Button>
        )}
      </div>
      {forge.status === 'failed' && forge.failureReason && (
        <p className="text-xs text-destructive">{forge.failureReason}</p>
      )}
      <label className="flex items-center justify-between gap-3 text-sm">
        <span className="flex flex-col">
          <span>Model</span>
          <span className="text-xs text-muted-foreground">{tierOption.hint}</span>
        </span>
        <select
          value={modelTier}
          disabled={isMutating}
          aria-label="Model tier"
          className={SELECT_CLASS}
          onChange={(e) => {
            const next = e.target.value as ForgeModelTier;
            // The default tier is stored as "no opt-in" (null) on the row.
            onSetModelTier(next === 'balanced' ? null : next);
          }}
        >
          {FORGE_MODEL_TIER_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
      <label className="flex items-center justify-between gap-3 text-sm">
        <span className="flex flex-col">
          <span>Size</span>
          <span className="text-xs text-muted-foreground">
            {sizeLocked ? 'Stop the forge to change its size. ' : ''}
            {sizeOption.hint}
          </span>
        </span>
        <select
          value={forge.size}
          disabled={isMutating || sizeLocked}
          aria-label="Forge size"
          className={SELECT_CLASS}
          onChange={(e) => onSetSize(e.target.value as ForgeSize)}
        >
          {FORGE_SIZE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}
